/**
 * Smart Card Manager + Editor — Owner Dashboard
 * Routes:
 *   /smart-card/manage  — list user's smart cards + create button
 *   /smart-card/edit    — create new
 *   /smart-card/edit/:id — edit existing
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit3, ExternalLink, Eye, Copy, ChevronLeft, Save, Sparkles,
  Phone, MessageCircle, MapPin, ImagePlus, Layers,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const DEFAULT_COLLECTIONS = [];

const ICON_OPTIONS = ['ring','gem','link','coins','utensils','coffee','scissors','stethoscope','scale-balanced','wrench','shirt','cookie-bite','dumbbell','camera','house-chimney','plane','leaf','graduation-cap','paw','screwdriver-wrench','user-tie','candy-cane','bolt','basket-shopping','circle-dot'];

// ============ Manager (list page) ============
export const SmartCardManager = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/smart-card/me');
      setCards(r.data.cards || []);
    } catch (e) {
      toast.error('Failed to load cards');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this Smart Card permanently?')) return;
    try {
      await api.delete(`/smart-card/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/smart/${slug}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Link copied'),
      () => toast.error('Could not copy')
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen pt-24 px-4 flex items-center justify-center bg-slate-950 text-white">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <div className="text-xl font-bold">Please login</div>
          <Link to="/login" className="mt-4 inline-block px-5 py-2 bg-amber-500 rounded-lg text-white">Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white" data-testid="smart-card-manager">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="text-amber-400 text-sm font-bold uppercase tracking-widest">Anant · Smart Business Cards</div>
            <h1 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-amber-300 via-pink-300 to-violet-300 bg-clip-text text-transparent mt-1">
              My Smart Cards
            </h1>
            <p className="text-slate-400 text-sm mt-1">Pick from 23 business templates · share via WhatsApp · floating chat CTA built-in.</p>
          </div>
          <button
            onClick={() => navigate('/smart-card/edit')}
            data-testid="smart-card-create-btn"
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Smart Card
          </button>
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : cards.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
            <Sparkles className="w-10 h-10 text-amber-300 mx-auto mb-3" />
            <div className="text-lg font-bold">No Smart Cards yet</div>
            <div className="text-slate-400 text-sm mt-1">Create your first one — choose any of the 23 templates for jewellers, salons, doctors, gyms, sweet shops & more.</div>
            <button
              onClick={() => navigate('/smart-card/edit')}
              data-testid="smart-card-create-empty"
              className="mt-5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold"
            >
              Create your first card
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((c) => (
              <div key={c.id} data-testid={`smart-card-item-${c.id}`} className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/10 flex items-center justify-center">
                    {c.logo_url ? <img src={c.logo_url} alt={c.business_name} className="w-full h-full object-cover" /> : <span className="text-xl font-bold text-amber-300">{c.business_name?.[0]}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white truncate">{c.business_name}</div>
                    <div className="text-xs text-slate-400 truncate">/smart/{c.slug}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">{c.template_id}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {c.view_count || 0}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Link to={`/smart/${c.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`smart-card-open-${c.id}`} className="px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-xs font-bold flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Open
                  </Link>
                  <button onClick={() => copyLink(c.slug)} data-testid={`smart-card-copy-${c.id}`} className="px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-xs font-bold flex items-center gap-1">
                    <Copy className="w-3 h-3" /> Link
                  </button>
                  <button onClick={() => navigate(`/smart-card/edit/${c.id}`)} data-testid={`smart-card-edit-${c.id}`} className="px-2.5 py-1.5 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/40 text-xs font-bold flex items-center gap-1">
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => onDelete(c.id)} data-testid={`smart-card-delete-${c.id}`} className="px-2.5 py-1.5 rounded-lg bg-rose-500/20 text-rose-200 border border-rose-500/40 text-xs font-bold flex items-center gap-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Editor (create + update) ============
export const SmartCardEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    template_id: 'jeweller_gold',
    tagline: '',
    location_text: '',
    maps_url: '',
    phone: '',
    whatsapp: '',
    cover_image_url: '',
    logo_url: '',
    collections: DEFAULT_COLLECTIONS,
    slug: '',
  });

  useEffect(() => {
    api.get('/smart-card/templates').then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    if (isEdit) {
      api.get(`/smart-card/${id}`)
        .then((r) => setForm({ ...r.data, slug: r.data.slug || '' }))
        .catch(() => toast.error('Could not load card'));
    }
  }, [id, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setColl = (idx, k, v) => setForm((f) => {
    const arr = [...(f.collections || [])];
    arr[idx] = { ...arr[idx], [k]: v };
    return { ...f, collections: arr };
  });
  const addColl = () => setForm((f) => ({ ...f, collections: [...(f.collections || []), { icon: 'circle-dot', label: 'New Item' }] }));
  const delColl = (idx) => setForm((f) => ({ ...f, collections: (f.collections || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.business_name?.trim()) { toast.error('Business name required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/smart-card/${id}`, form);
        toast.success('Saved');
      } else {
        const r = await api.post('/smart-card', form);
        toast.success('Smart Card created');
        navigate(`/smart-card/edit/${r.data.card.id}`);
        return;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const tpl = templates.find((t) => t.id === form.template_id) || {};

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white" data-testid="smart-card-editor">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/smart-card/manage')} className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to My Cards
        </button>

        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="text-amber-400 text-sm font-bold uppercase tracking-widest">Smart Card · {isEdit ? 'Edit' : 'Create'}</div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white">{form.business_name || 'Untitled Card'}</h1>
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="smart-card-save"
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold shadow-lg disabled:opacity-60 flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Card'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* ===== Left form ===== */}
          <div className="space-y-5">
            {/* Template picker */}
            <Section title="Choose Template" subtitle={`${templates.length} business templates`} icon={Layers}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setField('template_id', t.id)}
                    data-testid={`smart-card-template-${t.id}`}
                    className={`p-2 rounded-lg border text-left transition ${form.template_id === t.id ? 'border-amber-400 ring-2 ring-amber-400/40' : 'border-white/10 hover:border-white/30'}`}
                    style={{ background: t.bg, color: t.text_dark }}
                  >
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: t.accent }}>{t.category}</div>
                    <div className="text-xs font-bold mt-0.5 truncate" style={{ color: t.text_dark }}>{t.name}</div>
                    <div className="mt-1.5 h-1 rounded-full" style={{ background: t.accent }} />
                  </button>
                ))}
              </div>
            </Section>

            {/* Basics */}
            <Section title="Business Info">
              <Field label="Business Name *" data-testid="field-business-name">
                <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" value={form.business_name} onChange={(e) => setField('business_name', e.target.value)} placeholder="Gogate Jewellers" />
              </Field>
              <Field label="Tagline">
                <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" value={form.tagline} onChange={(e) => setField('tagline', e.target.value)} placeholder="Crafting Trust & Purity Since Generations" />
              </Field>
              <Field label="Location (text)">
                <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" value={form.location_text} onChange={(e) => setField('location_text', e.target.value)} placeholder="Paratwada, Maharashtra" />
              </Field>
              <Field label="Google Maps URL (optional)">
                <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" value={form.maps_url} onChange={(e) => setField('maps_url', e.target.value)} placeholder="https://maps.app.goo.gl/…" />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Phone">
                  <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="+919876543210" />
                </Field>
                <Field label="WhatsApp">
                  <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" value={form.whatsapp} onChange={(e) => setField('whatsapp', e.target.value)} placeholder="9876543210" />
                </Field>
              </div>
            </Section>

            {/* Images */}
            <Section title="Images" icon={ImagePlus}>
              <Field label="Cover Image URL">
                <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" value={form.cover_image_url} onChange={(e) => setField('cover_image_url', e.target.value)} placeholder="https://…" />
              </Field>
              <Field label="Logo URL">
                <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" value={form.logo_url} onChange={(e) => setField('logo_url', e.target.value)} placeholder="https://…" />
              </Field>
              {isEdit && (
                <Field label="Slug (URL)">
                  <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white opacity-70" value={form.slug || ''} disabled />
                  <div className="text-[10px] text-slate-500 mt-1">Slug is locked once created.</div>
                </Field>
              )}
            </Section>

            {/* Collections */}
            <Section title="Collections / Services (max 6)">
              <div className="space-y-2">
                {(form.collections || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`coll-row-${i}`}>
                    <select className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-white text-xs" value={c.icon} onChange={(e) => setColl(i, 'icon', e.target.value)}>
                      {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                    <input className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" value={c.label} onChange={(e) => setColl(i, 'label', e.target.value)} placeholder="Label" />
                    <button onClick={() => delColl(i)} data-testid={`coll-del-${i}`} className="p-2 rounded-lg bg-rose-500/15 text-rose-300 border border-rose-500/30"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {(form.collections || []).length < 6 && (
                  <button onClick={addColl} data-testid="coll-add" className="w-full py-2 rounded-lg border-2 border-dashed border-white/15 text-xs text-slate-400 hover:bg-white/5 flex items-center justify-center gap-1">
                    <Plus className="w-3 h-3" /> Add collection
                  </button>
                )}
              </div>
            </Section>
          </div>

          {/* ===== Right preview ===== */}
          <div className="lg:sticky lg:top-24 self-start">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Live Preview · {tpl.name || form.template_id}</div>
            <div className="rounded-2xl overflow-hidden border border-white/10" style={{ background: tpl.bg || '#fff' }}>
              <MiniPreview card={form} tpl={tpl} />
            </div>
            <div className="text-[11px] text-slate-500 mt-2">URL after save: <code>/smart/{form.slug || (form.business_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}</code></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Helpers ============
const Section = ({ title, subtitle, icon: Icon, children }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-4 h-4 text-amber-300" />}
      <div>
        <div className="font-bold text-white text-sm">{title}</div>
        {subtitle && <div className="text-[10px] text-slate-500 uppercase tracking-wider">{subtitle}</div>}
      </div>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1 font-semibold">{label}</div>
    {children}
  </label>
);

const MiniPreview = ({ card, tpl }) => {
  const accent = tpl?.accent || '#D4AF37';
  const textDark = tpl?.text_dark || '#1a1a1a';
  return (
    <div className="p-4" style={{ color: textDark, fontFamily: `'${tpl?.font || 'Inter'}', sans-serif` }} data-testid="smart-card-preview">
      <div className="h-24 w-full rounded-lg bg-cover bg-center mb-[-32px]" style={{ backgroundImage: card.cover_image_url ? `url(${card.cover_image_url})` : `linear-gradient(135deg, ${accent}, ${textDark})` }} />
      <div className="flex justify-center">
        <div className="w-20 h-20 rounded-full border-4 border-white shadow flex items-center justify-center overflow-hidden" style={{ background: '#fff' }}>
          {card.logo_url ? <img src={card.logo_url} alt="" className="w-full h-full object-cover" /> : <span className="text-2xl font-bold" style={{ color: accent }}>{(card.business_name || 'A')[0]}</span>}
        </div>
      </div>
      <div className="text-center mt-2">
        <div className="font-bold text-lg" style={{ color: textDark }}>{card.business_name || 'Business Name'}</div>
        {card.location_text && <div className="text-xs mt-0.5 flex items-center justify-center gap-1" style={{ color: accent }}><MapPin className="w-3 h-3" /> {card.location_text}</div>}
        {card.tagline && <div className="text-[11px] italic mt-1.5 opacity-70">&ldquo;{card.tagline}&rdquo;</div>}
      </div>
      <div className="flex justify-center gap-3 mt-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: accent, color: '#fff' }}><Phone className="w-3.5 h-3.5" /></div>
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#25D366', color: '#fff' }}><MessageCircle className="w-3.5 h-3.5" /></div>
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: accent, color: '#fff' }}><MapPin className="w-3.5 h-3.5" /></div>
      </div>
      {(card.collections || []).length > 0 && (
        <div className="grid grid-cols-2 gap-2 mt-4">
          {(card.collections || []).slice(0, 4).map((c, i) => (
            <div key={i} className="rounded-lg p-2 text-center border text-[11px]" style={{ background: accent + '20', borderColor: accent + '60', color: textDark }}>
              <i className={`fa-solid fa-${c.icon || 'circle-dot'}`} style={{ color: accent }} />
              <div className="mt-0.5 truncate">{c.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SmartCardManager;
